import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Lock, Save, CheckCircle2, Shield, ExternalLink, KeyRound, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Password Vault — compact single-row card that lets the client (optionally)
 * hand over Experian / Credit Karma / Identity IQ logins so Credit Quack can
 * start pulling reports without another back-and-forth. Passwords are Fernet
 * encrypted at rest on the server; admins have a separate audited peek.
 */

const PROVIDERS = [
  {
    key: "experian",
    name: "Experian",
    tint: "#1F3765",
    tintBg: "bg-blue-50 border-blue-200",
    logo: "https://customer-assets-jt897jd0.emergentagent.net/job_quack-dashboard-pro/artifacts/nt7lhefa_Experian_logo.svg.webp",
    signupUrl:  "https://www.experian.com/consumer-products/signup.html",
    resetUrl:   "https://usa.experian.com/register/passwordReset",
  },
  {
    key: "credit_karma",
    name: "Credit Karma",
    tint: "#00A94F",
    tintBg: "bg-emerald-50 border-emerald-200",
    logo: "https://customer-assets-jt897jd0.emergentagent.net/job_quack-dashboard-pro/artifacts/80qs6kqe_Credit_Karma_logo.svg.webp",
    signupUrl:  "https://www.creditkarma.com/signup",
    resetUrl:   "https://www.creditkarma.com/auth/logon/recover-password",
  },
  {
    key: "identity_iq",
    name: "Identity IQ",
    tint: "#C6262E",
    tintBg: "bg-rose-50 border-rose-200",
    logo: "https://customer-assets-jt897jd0.emergentagent.net/job_quack-dashboard-pro/artifacts/j4d9u3nc_IdentityIQ-logo.webp",
    signupUrl:  "https://www.identityiq.com/",
    resetUrl:   "https://member.identityiq.com/forgot-password.aspx",
  },
];

export default function CreditBureauVaultCard() {
  const [vault, setVault] = useState(null);
  const [expanded, setExpanded] = useState(null);      // provider key
  const [drafts, setDrafts] = useState({});
  const [reveal, setReveal] = useState({});
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/client/credit-credentials");
      setVault(data);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (key) => {
    const p = vault?.providers?.[key] || {};
    setDrafts((d) => ({ ...d, [key]: { username: p.username || "", password: p.password || "" } }));
    setExpanded(key);
  };

  const saveProvider = async (key) => {
    setBusy(true);
    try {
      const { data } = await api.post("/client/credit-credentials", { [key]: drafts[key] || {} });
      setVault(data);
      toast.success(`${PROVIDERS.find((p) => p.key === key).name} saved`);
      setExpanded(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const clearProvider = async (key) => {
    if (!window.confirm(`Remove your ${PROVIDERS.find((p) => p.key === key).name} login?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/client/credit-credentials", { [key]: { username: "", password: "" } });
      setVault(data);
      toast.success("Removed");
      setExpanded(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Remove failed");
    } finally { setBusy(false); }
  };

  const savedCount = useMemo(() => {
    const map = vault?.providers || {};
    return PROVIDERS.filter((p) => (map[p.key]?.username || map[p.key]?.has_password)).length;
  }, [vault]);

  return (
    <Card className="p-3 sm:p-4 rounded-2xl border-slate-100" data-testid="credit-vault-card">
      {/* Compact header */}
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
          <Shield size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-black text-base sm:text-lg text-slate-900 leading-tight">Password Vault</h3>
            <span className="text-[9px] uppercase tracking-widest font-black text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
              <Lock size={8} className="inline mr-0.5" /> Encrypted
            </span>
            <span className="text-[9px] uppercase tracking-widest font-black text-slate-600 bg-slate-100 rounded-full px-1.5 py-0.5">
              {savedCount}/3 linked
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5 hidden sm:block">
            Optional — hand us your bureau logins so we can start disputes without asking twice.
          </p>
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-slate-400 hover:text-slate-900 transition"
          data-testid="credit-vault-toggle"
          title={open ? "Collapse" : "Expand"}
        >
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {open && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {PROVIDERS.map((p) => {
            const saved = vault?.providers?.[p.key] || {};
            const isExp = expanded === p.key;
            const linked = !!(saved.username || saved.has_password);
            return (
              <div
                key={p.key}
                className={`rounded-xl border p-2.5 transition ${linked ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-white"} ${isExp ? "sm:col-span-3 sm:!bg-slate-50/60" : ""}`}
                data-testid={`credit-vault-provider-${p.key}`}
              >
                {/* Compact row */}
                <div className="flex items-center gap-2.5">
                  <div className={`h-11 flex-none rounded-md ${p.tintBg} border overflow-hidden flex items-center justify-center px-2`} style={{ width: 80 }}>
                    <img
                      src={p.logo}
                      alt={p.name}
                      className="max-h-6 max-w-full object-contain select-none"
                      draggable={false}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    {linked ? (
                      <>
                        <p className="text-[11px] font-bold text-slate-900 truncate">{saved.username || "—"}</p>
                        <p className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                          <CheckCircle2 size={10} className="text-emerald-600" />
                          {saved.has_password ? "Password saved" : "Add password"}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-[11px] font-bold text-slate-900">{p.name}</p>
                        <p className="text-[10px] text-slate-500">Not linked yet</p>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => isExp ? setExpanded(null) : startEdit(p.key)}
                    className="h-7 px-2 rounded-md border border-slate-200 text-[11px] font-bold text-slate-700 hover:border-slate-400 hover:text-slate-900 transition inline-flex items-center gap-1"
                    data-testid={`credit-vault-edit-${p.key}`}
                  >
                    <KeyRound size={11} /> {isExp ? "Cancel" : linked ? "Edit" : "Add"}
                  </button>
                </div>

                {/* Inline expand form */}
                {isExp && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-12 gap-2" data-testid={`credit-vault-form-${p.key}`}>
                    <div className="sm:col-span-5">
                      <Label className="text-[10px] uppercase tracking-widest font-black text-slate-500">Username / email</Label>
                      <Input
                        value={drafts[p.key]?.username || ""}
                        onChange={(e) => setDrafts((d) => ({ ...d, [p.key]: { ...d[p.key], username: e.target.value } }))}
                        className="h-9 text-sm mt-1"
                        autoComplete="off"
                        data-testid={`credit-vault-username-${p.key}`}
                      />
                    </div>
                    <div className="sm:col-span-5">
                      <Label className="text-[10px] uppercase tracking-widest font-black text-slate-500">Password</Label>
                      <div className="relative mt-1">
                        <Input
                          type={reveal[p.key] ? "text" : "password"}
                          value={drafts[p.key]?.password || ""}
                          onChange={(e) => setDrafts((d) => ({ ...d, [p.key]: { ...d[p.key], password: e.target.value } }))}
                          className="h-9 text-sm pr-9"
                          autoComplete="new-password"
                          data-testid={`credit-vault-password-${p.key}`}
                        />
                        <button
                          type="button"
                          onClick={() => setReveal((r) => ({ ...r, [p.key]: !r[p.key] }))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900"
                          aria-label="Toggle password visibility"
                        >
                          {reveal[p.key] ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      </div>
                    </div>
                    <div className="sm:col-span-2 flex items-end gap-1">
                      <Button
                        size="sm"
                        onClick={() => saveProvider(p.key)}
                        disabled={busy}
                        className="flex-1 h-9 bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid={`credit-vault-save-${p.key}`}
                      >
                        <Save size={11} className="mr-1" /> {busy ? "…" : "Save"}
                      </Button>
                    </div>
                    <div className="sm:col-span-12 flex items-center gap-3 text-[10px] pt-0.5">
                      <a href={p.resetUrl} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid={`credit-vault-reset-${p.key}`}>
                        Reset password <ExternalLink size={9} />
                      </a>
                      <a href={p.signupUrl} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid={`credit-vault-signup-${p.key}`}>
                        Don't have an account? Sign up <ExternalLink size={9} />
                      </a>
                      {linked && (
                        <button onClick={() => clearProvider(p.key)} disabled={busy} className="ml-auto text-rose-600 hover:text-rose-700 font-bold" data-testid={`credit-vault-clear-${p.key}`}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
